import { useState } from "react";
import saveIcon from "../assets/save-icon.svg";

const YesNoField = ({ label, value, onChange }) => (
  <div className="w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[14px] pr-[4px] flex items-center gap-[8px]">
    <span className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] whitespace-nowrap min-w-0 truncate">
      {label}
    </span>
    <div className="ml-auto flex items-center gap-[2px] shrink-0">
      <button
        type="button"
        onClick={() => onChange("YES")}
        className={`box-border shrink-0 w-[58px] 3xl:w-[66px] h-[41px] rounded-[100px] px-0 py-0 border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
          value === "YES"
            ? "bg-[#5AC69F] text-white"
            : "bg-[#F5F5F5] text-[#000000]"
        }`}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange("NO")}
        className={`box-border shrink-0 w-[58px] 3xl:w-[66px] h-[41px] rounded-[100px] px-0 py-0 border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
          value === "NO"
            ? "bg-[#FF4343] text-white"
            : "bg-[#F5F5F5] text-[#000000]"
        }`}
      >
        No
      </button>
    </div>
  </div>
);

const SelectField = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  showFloatingLabel = true,
}) => (
  <div className="relative w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent">
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="absolute inset-0 w-full h-full pl-[32px] pr-[120px] bg-transparent border-none outline-none appearance-none cursor-pointer font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B]"
    >
      <option value="" disabled hidden>
        {placeholder || `${label}`}
      </option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    {showFloatingLabel && value && (
      <span className="absolute right-[45px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
        {label}
      </span>
    )}
    <div className="absolute right-[14px] top-1/2 -translate-y-1/2 pointer-events-none">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path
          d="M7 10.5L14 17.5L21 10.5"
          stroke="#5AC69F"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </div>
);

const PersonalHistoryFormAi = () => {
  const [formData, setFormData] = useState({
    diet: null,
    nutrition: null,
    appetite: null,
    bowels: null,
    knownAllergies: "NO",
    habitsAddictions: "NO",
    allergyDetails: null,
    addictionDetails: null,
  });

  const dietOptions = ["SPECIAL", "NORMAL"];
  const nutritionOptions = ["MALNOURISHED", "NORMAL"];
  const appetiteOptions = ["NORMAL", "LOW", "EXCESSIVE"];
  const bowelOptions = ["NORMAL", "CONSTIPATION", "LOOSE"];
  const addictionOptions = ["ALCOHOL", "SMOKING", "DRUGS", "OTHERS"];

  const handleChange = (field) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="w-full">
      <div className="w-full h-0 border border-solid border-[#00000010] bg-[#00000010]" />{" "}
      <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 pt-[30px]">
        <SelectField
          label="Diet"
          value={formData.diet}
          onChange={handleChange("diet")}
          options={dietOptions}
        />
        <SelectField
          label="Nutrition"
          value={formData.nutrition}
          onChange={handleChange("nutrition")}
          options={nutritionOptions}
        />
        <SelectField
          label="Appetite"
          value={formData.appetite}
          onChange={handleChange("appetite")}
          options={appetiteOptions}
        />
        <SelectField
          label="Bowels"
          value={formData.bowels}
          onChange={handleChange("bowels")}
          options={bowelOptions}
        />

        <YesNoField
          label="Known Allergies"
          value={formData.knownAllergies}
          onChange={handleChange("knownAllergies")}
        />

        {formData.knownAllergies === "YES" && (
          <div className="relative w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent px-[32px] flex items-center">
            <input
              type="text"
              placeholder="Allergic to penicillin"
              value={formData.allergyDetails ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  allergyDetails: e.target.value,
                }))
              }
              className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none shadow-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] placeholder:text-[#8B8B8B]"
            />
          </div>
        )}

        <YesNoField
          label="Habits Addictions"
          value={formData.habitsAddictions}
          onChange={handleChange("habitsAddictions")}
        />

        {formData.habitsAddictions === "YES" && (
          <SelectField
            label="Select"
            value={formData.addictionDetails ?? ""}
            onChange={handleChange("addictionDetails")}
            options={addictionOptions}
            placeholder="Select"
            showFloatingLabel={false}
          />
        )}
      </div>
      <div className="flex justify-end mt-[12px]">
        <button className="w-[170px] h-[74px] xl:w-[94.97px] xl:h-[40.97px]  2xl:w-[94.97px] 2xl:h-[40.97px] border border-none rounded-full bg-black flex justify-center items-center gap-[8px] font-lufga text-[14px] text-[#68FF9F] font-normal">
          SAVE
          <img src={saveIcon} alt="save" className="pl-[16px]" />
        </button>
      </div>
    </div>
  );
};

export default PersonalHistoryFormAi;
